import { Box, Card, CardContent, Typography } from '@mui/material'

import type { UserStats } from './analytics-utils'

type FunnelNodeProps = {
  label: string
  count: number
  parentCount: number
  color: string
  note?: string | undefined
}

function FunnelNode({ label, count, parentCount, color, note }: FunnelNodeProps) {
  const pct = parentCount > 0 ? ((count / parentCount) * 100).toFixed(1) : '0.0'
  return (
    <Card
      variant="outlined"
      sx={{
        minWidth: 130,
        bgcolor: color,
        borderColor: 'divider',
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="body2" fontWeight="bold">
          {label}
        </Typography>
        <Typography variant="h6" fontWeight="bold">
          {count}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {pct}% of parent
        </Typography>
        {note && (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
            {note}
          </Typography>
        )}
      </CardContent>
    </Card>
  )
}

type FunnelDiagramProps = {
  title: string
  stats: UserStats
  unverifiedNoPurchaseNote?: string
}

export function FunnelDiagram({ title, stats, unverifiedNoPurchaseNote }: FunnelDiagramProps) {
  const verified = stats.verifiedPurchased + stats.verifiedNotPurchased
  const unverified = stats.unverifiedPurchased + stats.unverifiedNotPurchased
  const connectorColor = 'grey.400'
  const connectorWidth = '2px solid'

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FunnelNode label="Total" count={stats.total} parentCount={stats.total} color="grey.100" />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', width: 40 }}>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-end' }}>
            <Box sx={{ width: '100%', borderBottom: connectorWidth, borderColor: connectorColor }} />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Box sx={{ height: 0, width: 0 }} />
          </Box>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-start' }}>
            <Box sx={{ width: '100%', borderTop: connectorWidth, borderColor: connectorColor }} />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, justifyContent: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <FunnelNode label="Verified" count={verified} parentCount={stats.total} color="success.50" />

            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', width: 32 }}>
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-end' }}>
                <Box sx={{ width: '100%', borderBottom: connectorWidth, borderColor: connectorColor }} />
              </Box>
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-start' }}>
                <Box sx={{ width: '100%', borderTop: connectorWidth, borderColor: connectorColor }} />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FunnelNode label="Purchased" count={stats.verifiedPurchased} parentCount={verified} color="#e8f5e9" />
              <FunnelNode label="No Purchase" count={stats.verifiedNotPurchased} parentCount={verified} color="#f1f8e9" />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <FunnelNode label="Unverified" count={unverified} parentCount={stats.total} color="warning.50" />

            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', width: 32 }}>
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-end' }}>
                <Box sx={{ width: '100%', borderBottom: connectorWidth, borderColor: connectorColor }} />
              </Box>
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-start' }}>
                <Box sx={{ width: '100%', borderTop: connectorWidth, borderColor: connectorColor }} />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FunnelNode label="Purchased" count={stats.unverifiedPurchased} parentCount={unverified} color="#fff3e0" />
              <FunnelNode label="No Purchase" count={stats.unverifiedNotPurchased} parentCount={unverified} color="#fafafa" note={unverifiedNoPurchaseNote} />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
